import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactElement, ReactNode } from "react";

import { Toast, interrupts } from "./Toast";
import type { ToastTone } from "./Toast";
import styles from "./Toast.module.css";

/** How long a notice stays before it dismisses itself. */
export const DEFAULT_TOAST_TIMEOUT_MS = 6000;

export interface ToastRequest {
  readonly tone: ToastTone;
  readonly message: string;
  /** `0` keeps the notice until it is dismissed by hand. */
  readonly timeoutMs?: number | undefined;
  /** One optional action ("Abrir"): pressing it runs `onSelect` and dismisses the notice; a notice with one never times out. */
  readonly action?: ToastAction | undefined;
}

export interface ToastAction {
  readonly label: string;
  readonly onSelect: () => void;
}

interface ToastEntry extends ToastRequest {
  readonly id: number;
}

export interface ToastContextValue {
  /** Show a notice. Returns its identifier, so a caller can dismiss it early. */
  readonly push: (request: ToastRequest) => number;
  readonly dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export interface ToastProviderProps {
  readonly children: ReactNode;
}

/**
 * The replacement for `alert()`, mounted once above the whole application.
 *
 * Notices live here rather than in the component that raised them because the
 * raiser usually unmounts — a container navigates away right after revoking a
 * session — and a notice that dies with its cause is a notice nobody reads.
 *
 * Every timer is tracked and cleared, on dismissal and on unmount: a pending
 * `setTimeout` that fires into an unmounted tree is the classic way a test
 * suite starts failing somewhere else.
 */
export function ToastProvider({ children }: ToastProviderProps): ReactElement {
  const [toasts, setToasts] = useState<readonly ToastEntry[]>([]);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  const nextId = useRef(0);

  const dismiss = useCallback((id: number): void => {
    const timer = timers.current.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((current) => current.filter((entry) => entry.id !== id));
  }, []);

  const push = useCallback(
    (request: ToastRequest): number => {
      const id = nextId.current;
      nextId.current += 1;
      setToasts((current) => [...current, { ...request, id }]);

      // A notice with an action waits for the person (WCAG 2.2.1): it never dismisses itself.
      const timeoutMs = request.action === undefined ? (request.timeoutMs ?? DEFAULT_TOAST_TIMEOUT_MS) : 0;
      if (timeoutMs > 0) {
        timers.current.set(
          id,
          setTimeout(() => {
            dismiss(id);
          }, timeoutMs),
        );
      }
      return id;
    },
    [dismiss],
  );

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) clearTimeout(timer);
      pending.clear();
    };
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ push, dismiss }), [push, dismiss]);

  const render = (entry: ToastEntry): ReactElement => (
    <Toast
      key={entry.id}
      tone={entry.tone}
      message={entry.message}
      action={
        entry.action === undefined
          ? undefined
          : {
              label: entry.action.label,
              onSelect: () => {
                dismiss(entry.id);
                entry.action?.onSelect();
              },
            }
      }
      onDismiss={() => {
        dismiss(entry.id);
      }}
    />
  );

  return (
    <ToastContext value={value}>
      {children}
      {/* Two live regions, mounted once and never removed (verifier m6): a notice is inserted into the
          one its tone picks, so it is announced on arrival instead of with the region itself. */}
      <div className={styles.region}>
        <div className={styles.stack} aria-live="polite" data-region="notices">
          {toasts.filter((entry) => !interrupts(entry.tone)).map(render)}
        </div>
        <div className={styles.stack}>
          {toasts.filter((entry) => interrupts(entry.tone)).map(render)}
        </div>
      </div>
    </ToastContext>
  );
}

/** The notice API, from the nearest provider. */
export function useToast(): ToastContextValue {
  const value = useContext(ToastContext);
  if (value === null) {
    throw new Error("useToast must be called inside a ToastProvider");
  }
  return value;
}
